import { getWithFullResponse } from '@citrite/http';
import { ctxConsoleLog } from '@citrite/msal-browser';
import { PlatformDependencies } from '@citrite/workspace-ui-platform';
import { environment } from 'Environment/Environment';
import { container } from 'Workspace/DependencyManagement';
import {
	getStoreConfigUrlParams,
	getStoreName,
	OnPremNativeOverrideParams,
} from 'Workspace/SchemeCallHandler/schemeCallHandler';

// allow-unused-export: for unit tests to access private methods of the singleton instance
export class CtxStoreWebUrl {
	private ctxStoreWebUrl: string;

	private static instance: CtxStoreWebUrl;

	private constructor() {
		// if the WSUI web view is already loaded on https:// url instead of file:// url
		// this saves our time to avoid extra receiverconfig.cr file download
		this.ctxStoreWebUrl = this.buildStoreWebUrlFromWebViewUrl();
	}

	public static getInstance(): CtxStoreWebUrl {
		if (!CtxStoreWebUrl.instance) {
			CtxStoreWebUrl.instance = new CtxStoreWebUrl();
		}
		return CtxStoreWebUrl.instance;
	}

	private isValidHttpsUrl = (url: string) =>
		typeof url === 'string' && url.toLowerCase().startsWith('https://');

	// This will only be needed in native apps since native\ onprem apps load WSUI webview as file://
	// and we need extra steps to find the exact url from the receiverconfig.cr file.
	public async initCachedStoreWebUrlForRedeemTicket(): Promise<string> {
		if (this.isValidHttpsUrl(this.ctxStoreWebUrl)) {
			ctxConsoleLog(
				'ctxStoreWebUrl is already initialized and valid: ',
				this.ctxStoreWebUrl
			);
			return this.ctxStoreWebUrl;
		}

		this.ctxStoreWebUrl = await this.getStoreWebUrlForRedeemTicket();
		ctxConsoleLog('Initialized ctxStoreWebUrl to: ', this.ctxStoreWebUrl);
		return this.ctxStoreWebUrl;
	}

	public getCachedStoreWebUrlForRedeemTicket(): string {
		if (!this.isValidHttpsUrl(this.ctxStoreWebUrl)) {
			throw new Error(
				'ERROR : Invalid ctxStoreWebUrl. Not initialized or invalid:' +
					this.ctxStoreWebUrl
			);
		}

		return this.ctxStoreWebUrl;
	}

	private buildStoreWebUrlFromWebViewUrl(): string {
		// If the current page is already on an HTTPS Store URL to reduce our cloud test efforts.
		if (location?.protocol?.toLowerCase().trim() === 'https:') {
			const ret = location.origin + this.getCitrixStoreWebPath(location.pathname);
			ctxConsoleLog(
				'Current page is on HTTPS, using it to build Store Web URL for RedeemTicket:',
				ret
			);
			return ret;
		}

		return null;
	}

	/**
	 * @param location
	 * @returns  https://subdomain.store.com/Citrix/<STORENAME>Web/
	 */
	private async getStoreWebUrlForRedeemTicket(): Promise<string> {
		try {
			const workspaceConfiguration = container
				.resolve(PlatformDependencies.WorkspaceConfiguration)
				.get();

			const onPremNativeOverrideParams: OnPremNativeOverrideParams = {
				storeName: getStoreName(environment.store.baseUri),
				fetchFunction: async (url: string) => {
					const respFromNative = await getWithFullResponse<string>(url, {});
					return respFromNative.data;
				},
			};

			ctxConsoleLog(
				'Fetching Store/Gateway URL from receiver configuration for store:',
				onPremNativeOverrideParams.storeName
			);

			const urls = await getStoreConfigUrlParams(
				workspaceConfiguration?.userInterface?.receiverConfiguration?.downloadURL,
				onPremNativeOverrideParams
			);

			ctxConsoleLog('Fetched Store/Gateway URL from receiver configuration:', urls);

			return this.buildRedeemTicketUrlFromReceiverConfig(urls);
		} catch (e) {
			ctxConsoleLog(
				'Error fetching store URL from receiver configuration, falling back to window location origin',
				e
			);
			return null;
		}
	}

	/**
	 * getStoreConfigUrlParams() returns an object as follows.
	 *
	 * NOTE that the word Web is missing from the StorePath in the storeUrl since this
	 * is how the URL is sent from the Native client and we need to add the "Web" suffix in our code before sending it to MSAL for RedeemTicket call.
	 *
	 * {
	 * 	 gatewayUrl: 'https://nsgoidcst.tpvazuredom.com/',
	 *   storeUrl: 'https://onpremsf2.tpvazuredom.com/Citrix/2603store1/discovery'
	 * }
	 *
	 *   For the RedeemTicket call we need to call the Gatewayurl + StorePath
	 *   so that actual url we need to send to MSAL is :
	 *
	 *   GatewayUrl + "/Citrix/<STORENAME>Web/"
	 *
	 *   example:
	 *   https://nsgoidcst.tpvazuredom.com/Citrix/2603store1Web/
	 *
	 * @param storeWebUrl
	 * @param redeemTicket
	 */
	private buildRedeemTicketUrlFromReceiverConfig(urls: {
		gatewayUrl?: string;
		storeUrl?: string;
	}): string {
		// Extract the store path from the store URL
		// since the storeUrl: 'https://onpremsf2.tpvazuredom.com/Citrix/2603store1/discovery'
		// lacks the Web suffix in the path, we need to add it back in our code before sending it to MSAL for RedeemTicket call.
		const storeUrl: URL = new URL(urls?.storeUrl);
		const storePath = this.getCitrixStoreWebPath(storeUrl.pathname, true);
		const origin = urls.gatewayUrl ? new URL(urls.gatewayUrl).origin : storeUrl.origin;
		const combinedUrl = `${origin}${storePath}`;
		return combinedUrl;
	}

	/**
	 *  This path will be eventually used to handle Citrix StoreFront URLs
	 *  and also for RedeemTicket which is always in the form of
	 *  "/Citrix/<STORE>Web/Tickets/RedeemTicket" where <STORE> can be any text.
	 *
	 *  @param pathname the path part of the URL to parse out the store path
	 *  @param appendWebSuffix whether to append "Web" suffix to the store path if it's missing. This is needed for the RedeemTicket call since the native client sends the store path without the Web suffix.
	 *  @returns the extracted Citrix Store path in the form of "/Citrix/<STORE>Web/" with trailing slash. If appendWebSuffix is false, it will return "/Citrix/<STORE>/" if the Web suffix is missing in the input pathname.
	 */
	private getCitrixStoreWebPath(pathname: string, appendWebSuffix = false): string {
		// Match pattern: /Citrix/<STORE>Web/ or /Citrix/<STORE>Web where <STORE> can be any text (case-insensitive)
		// For native apps the "Web" word can be missing. So in the following regex will match both cases with or without the Web word
		// and we will add the Web suffix in our code before sending it to MSAL for RedeemTicket call.
		const citrixStoreMatch = pathname.match(/^(\/Citrix\/[^\/]+)(?:Web)?\/?/i);

		if (citrixStoreMatch) {
			let matchedPath = citrixStoreMatch[1];

			// Append "Web" suffix if it's missing and the flag is set
			if (appendWebSuffix && matchedPath?.toLowerCase().endsWith('web') === false) {
				matchedPath += 'Web/';
			}

			// Always return with trailing slash for consistency
			return matchedPath.endsWith('/') ? matchedPath : matchedPath + '/';
		}

		// Fallback to original pathname if pattern doesn't match
		ctxConsoleLog(
			'!!!! POSSIBLE ERROR: /Citrix/<Store>Web path not found in pathname. RedeemTicket may fail',
			pathname
		);
		return pathname;
	}
}

export const ctxStoreWebUrlInstance = CtxStoreWebUrl.getInstance();
